/**
 * Textos de la interfaz en español (es-PE), separados del código para una traducción posterior
 * al inglés (doc. 02 §9). Los textos con datos son funciones.
 */

export const M = {
  app: {
    nombre: 'Gemba Studio',
    lema: 'Gemelos digitales para diseñar, simular y mejorar operaciones físicas.',
    version: (v: string, commit: string) => `Versión ${v} · ${commit}`,
    acercaDe: 'Acerca de',
    cargando: 'Cargando…',
    volverAlInicio: 'Volver al inicio',
    cerrar: 'Cerrar',
    cancelar: 'Cancelar',
    confirmar: 'Confirmar',
  },
  inicio: {
    titulo: 'Diseña, simula y mejora tu operación',
    subtitulo:
      'Modela el taller, simula una jornada con reglas explícitas y decide con indicadores calculados por el motor.',
    empezar: 'Empieza aquí',
    tallerBase: 'Taller base',
    tallerBaseDesc:
      'Plantilla ficticia lista para simular: 6 elevadores, 6 técnicos, 2 recepciones, 6 cajones de patio, lavado y 21 autos al día.',
    crearTallerBase: 'Crear desde Taller base',
    crearSobrecargado: 'Ejemplo sobrecargado (38 autos al día)',
    sobrecargadoDesc:
      'El mismo taller con la demanda de la entrega 1.0: un taller saturado, para comparar.',
    lienzo: 'Lienzo en blanco',
    lienzoDesc:
      'Responde unas preguntas («Configura tu taller») y recibe un taller listo para simular, o empieza con un espacio vacío.',
    crearLienzo: 'Crear lienzo',
    importar: 'Importar archivo',
    importarDesc:
      'Abre un proyecto exportado (.gemba.json) o un archivo del prototipo v6 (.json). Se valida antes de guardarlo.',
    importadoV6Titulo: 'Archivo del v6 convertido',
    importadoV6Texto: (nombre: string, espacios: number, elementos: number) =>
      `«${nombre}» se convirtió desde el prototipo v6: ${espacios} ${espacios === 1 ? 'espacio' : 'espacios'} y ${elementos} ${elementos === 1 ? 'elemento' : 'elementos'}. Los parámetros quedaron como supuestos, con la nota «Valor del v6».`,
    importadoV6Revisa: 'Para revisar:',
    abrirProyecto: 'Abrir el proyecto',
    quedarmeEnElInicio: 'Quedarme en el inicio',
    tusProyectos: 'Tus proyectos',
    vacio: 'Aún no tienes proyectos.',
    vacioSiguiente: 'Empieza con «Taller base»: ya valida y simula. Luego cambia lo que quieras.',
    abrir: 'Abrir',
    exportar: 'Exportar',
    eliminar: 'Eliminar',
    eliminarTitulo: (n: string) => `¿Eliminar «${n}»?`,
    eliminarTexto:
      'El proyecto y su historial de versiones se borrarán de este navegador y no se pueden recuperar. Si quieres conservarlo, expórtalo antes.',
    eliminado: (n: string) => `Se eliminó «${n}».`,
    actualizado: (fecha: string) => `Actualizado ${fecha}`,
    elementos: (n: number) => `${n} ${n === 1 ? 'elemento' : 'elementos'}`,
    diagnostico: 'Diagnóstico: rendimiento y verificación del motor',
    privacidad:
      'Tus proyectos viven solo en este navegador y este equipo: otro navegador no los ve y borrar los datos del sitio los elimina. Exporta un respaldo cada semana y antes de cada presentación.',
    importado: (n: string) => `Se importó «${n}».`,
    importadoMigrado: (n: string, v: number) =>
      `Se importó «${n}» y se actualizó desde el esquema ${v}.`,
    importadoCopia: (n: string) =>
      `Ya existía un proyecto con el mismo identificador: «${n}» se importó como copia.`,
    origen: {
      'plantilla:taller-base': 'Taller base',
      asistente: 'Configura tu taller',
      lienzo: 'Lienzo',
      importado: 'Importado',
      duplicado: 'Duplicado',
      caso: 'Caso de verificación',
    } as Record<string, string>,
  },
  proyecto: {
    noEncontrado: 'No encontramos este proyecto en este navegador.',
    errorAbrir: 'No se pudo abrir el proyecto.',
    nombre: 'Nombre del proyecto',
    editar: 'Editar',
    simular: 'Simular',
    deshacer: (n: string) => `Deshacer: ${n}`,
    rehacer: (n: string) => `Rehacer: ${n}`,
    nadaQueDeshacer: 'Nada que deshacer',
    nadaQueRehacer: 'Nada que rehacer',
    exportar: 'Exportar',
    exportado: (archivo: string) => `Se descargó ${archivo}.`,
    guardado: 'Guardado',
    guardando: 'Guardando…',
    pendiente: 'Cambios sin guardar',
    errorGuardado: 'Sin guardar',
    reintentar: 'Reintentar',
    vistaIso: 'Vista isométrica',
    vistaPlanta: 'Vista en planta',
    vistaPlano: 'Plano 2D',
    vistaPlanoAyuda:
      'Plano 2D: vista desde arriba, como un plano de arquitectura, con los colores del semáforo; casi no usa la tarjeta gráfica. Se edita y se simula igual.',
    centrar: 'Centrar el espacio',
  },
  catalogo: {
    titulo: 'Catálogo',
    ayuda: 'Elige un elemento y haz clic en el plano para colocarlo.',
    simula: 'Simula',
    simulaAyuda: 'Afecta a la simulación',
    decorativo: 'Solo visual',
    decorativoAyuda: (desde?: string) =>
      desde
        ? `No cambia los resultados todavía: se simula desde el paso ${desde}.`
        : 'No cambia los resultados: es parte del aspecto del espacio.',
    categorias: 'Categorías del catálogo',
    buscar: 'Buscar en el catálogo',
    buscarEjemplo: 'Buscar: elevador, árbol…',
    limpiarBusqueda: 'Limpiar la búsqueda',
    resultados: (n: number) =>
      n === 0 ? 'Nada coincide con la búsqueda.' : `${n} ${n === 1 ? 'resultado' : 'resultados'}`,
    medidas: (ancho: string, fondo: string | null) =>
      fondo === null ? `${ancho} m de ancho` : `${ancho} × ${fondo} m`,
    viaDoble: 'Vía de doble sentido',
    viaUnSentido: 'Vía de un sentido',
    sendaPeatonal: 'Senda peatonal',
    viaAyuda: 'Haz clic en el plano para marcar los puntos de la calzada.',
    colocando: (n: string) => `Colocando: ${n}. Haz clic en el plano · Esc para cancelar`,
    seDibuja: 'Se dibuja',
    colocarCentro: 'Colocar en el centro de la vista',
  },
  via: {
    dibujando: (tipo: string) => `Dibujando ${tipo}`,
    puntos: (n: number, m: string) => `${n} ${n === 1 ? 'punto' : 'puntos'} · ${m} m`,
    terminar: 'Terminar vía',
    dibujandoMuro: 'Dibujando muro',
    dibujandoZona: 'Dibujando zona de piso',
    terminarMuro: 'Terminar muro',
    cerrarZona: 'Cerrar zona',
    puntosArea: (n: number, m2: string) => `${n} ${n === 1 ? 'punto' : 'puntos'} · ${m2} m²`,
    a45: 'Mayús: ángulos de 45°',
    deshacerPunto: 'Deshacer punto',
    salir: 'Salir',
    minimo: 'Una vía necesita al menos 2 puntos.',
    iman: 'Empalme: el punto se une a la otra vía',
    imanAyuda:
      'Al acercar un punto a otra vía, se une solo (imán). Mantén Alt para dejarlo sin imán.',
  },
  cotas: {
    ver: 'Ver cotas',
    ayuda:
      'Cotas en metros: las medidas del espacio en sus bordes y, con algo seleccionado, sus medidas y la distancia libre a lo más cercano (otro elemento, una vía o el borde).',
    metros: (m: string) => `${m} m`,
    desplazamiento: (dx: string, dz: string) => `Δ x ${dx} · z ${dz} m`,
  },
  medir: {
    boton: 'Medir',
    ayuda: 'Medir distancias en línea recta (tecla M). Esc para salir.',
    activa: 'Midiendo: haz clic en dos puntos del plano · Esc para salir',
    salir: 'Salir',
    resultado: (m: string, tiempo: string, kmh: string, origen: string) =>
      `Medida: ${m} m en línea recta · ${tiempo} en auto a ${kmh} km/h (${origen})`,
    primerPunto: 'Medir: haz clic en el punto de partida',
    segundos: (s: number) =>
      s < 60 ? `${s} s` : `${Math.floor(s / 60)} min${s % 60 ? ` ${s % 60} s` : ''}`,
  },
  bocas: {
    desde: (origen: 'ingreso' | 'patio', codigo: string, metros: string | null, tiempo: string) =>
      `Desde ${origen === 'ingreso' ? 'el ingreso' : `el patio (${codigo})`}: ${metros === null ? '' : `${metros} m por la ruta · `}${tiempo} en auto`,
    ver: 'Ver bocas',
    ayuda:
      'Bocas de acceso: verde si conectan con una vía, rojo si no. Al colocar un elemento, su boca mira sola a la vía más cercana.',
    resumen: (conectan: number, sin: number) =>
      `${conectan} ${conectan === 1 ? 'conecta' : 'conectan'} · ${sin} sin acceso`,
    orientar: 'Orientar hacia la vía',
    conecta: (lado: string, m: string) => `Boca de acceso (${lado}): conecta con la vía a ${m} m.`,
    sinAcceso: (lado: string) => `Boca de acceso (${lado}): no llega a ninguna vía.`,
    salida: (m: string) => `Salida por delante: conecta con la vía a ${m} m.`,
    lado: { detras: 'detrás', delante: 'delante', centro: 'sobre la vía' },
  },
  edicion: {
    unicaReubicada: (nombre: string) =>
      `Se reubicó «${nombre}»: solo puede haber uno de su tipo por espacio.`,
    nadaQuePegar: 'El portapapeles está vacío: copia algo con Ctrl+C.',
    crearFilaDe: (n: number) => `Crear fila de ${n}`,
    alinear: 'Alinear',
    distribuir: 'Distribuir',
    izquierda: 'Izquierda',
    centroX: 'Centro',
    derecha: 'Derecha',
    arriba: 'Arriba',
    centroZ: 'Medio',
    abajo: 'Abajo',
    horizontal: 'Horizontal',
    vertical: 'Vertical',
    alinearAyuda: 'Con el borde o el centro de la selección, vista en planta.',
    distribuirAyuda: 'La misma separación libre entre todos; los de los extremos no se mueven.',
    duplicar: 'Duplicar',
    copiar: 'Copiar',
    fila: 'Crear fila',
    filaCantidad: 'Copias',
    filaPaso: 'Paso (m)',
    filaSentido: 'Hacia',
    sentidos: { este: '→ derecha', oeste: '← izquierda', sur: '↓ abajo', norte: '↑ arriba' },
    filaAyuda:
      'Copias en línea, con el mismo giro. El paso es la distancia entre centros: el propuesto es el de su fila o su ancho más 0.5 m.',
    guias: (alineados: number, separacion: string | null) =>
      [
        alineados > 0
          ? `Alineado con ${alineados} ${alineados === 1 ? 'elemento' : 'elementos'}`
          : '',
        separacion !== null ? `misma separación (${separacion} m)` : '',
      ]
        .filter(Boolean)
        .join(' · '),
  },
  /** Coordenadas del cursor en el plano (barra de estado del lienzo). */
  cursor: (x: string, z: string) => `x ${x} · z ${z} m`,
  propiedades: {
    titulo: 'Propiedades',
    nada: 'Selecciona un elemento, una vía, un piso o un muro en el plano para ver y editar sus propiedades.',
    atajos: 'Atajos de teclado: botón «Atajos» de la barra de estado o tecla ?.',
    nombrePieza: 'Nombre (opcional)',
    x: 'Posición X (m)',
    z: 'Posición Z (m)',
    giro: 'Giro (°)',
    ancho: 'Ancho (m)',
    fondo: 'Fondo (m)',
    alto: 'Alto (m)',
    tipoBahia: 'Tipo de elevador',
    girar: 'Girar 90°',
    eliminar: 'Eliminar',
    varios: (n: number) => `${n} elementos seleccionados`,
    via: 'Vía',
    piso: 'Piso',
    muro: 'Muro',
    acabado: 'Acabado',
    areaPiso: (m2: string, perimetro: string) => `Área ${m2} m² · perímetro ${perimetro} m`,
    ayudaPiso:
      'Arrastra las manijas blancas para mover sus esquinas y los «+» para agregar una. Supr borra la esquina seleccionada (un piso necesita 3). Arrastrarlo ya seleccionado lo mueve entero. No cambia la simulación.',
    espesor: 'Espesor (m)',
    altoMuro: 'Alto (m)',
    ayudaMuro:
      'Arrastra las manijas blancas para mover sus puntos y los «+» para agregar uno. Arrastrarlo ya seleccionado lo mueve entero. No cambia la simulación: los autos siguen las vías.',
    tipoVia: 'Tipo de vía',
    invertir: 'Invertir sentido',
    largo: (m: string, n: number) => `Largo ${m} m · ${n} puntos`,
    puntosVia:
      'Arrastra un punto blanco para moverlo y un «+» para insertar uno. Para borrar un punto, selecciónalo y pulsa Supr o «Borrar punto».',
    puntoSeleccionado: (i: number, n: number) => `Punto ${i} de ${n}`,
    puntoX: 'Punto X (m)',
    puntoZ: 'Punto Z (m)',
    borrarPunto: 'Borrar punto',
    minimoPuntos: 'Una vía necesita al menos 2 puntos: si ya no la necesitas, elimínala.',
    minimoPuntosDe: {
      via: 'Una vía necesita al menos 2 puntos: si ya no la necesitas, elimínala.',
      piso: 'Un piso necesita al menos 3 puntos: si ya no lo necesitas, elimínalo.',
      muro: 'Un muro necesita al menos 2 puntos: si ya no lo necesitas, elimínalo.',
    },
    extremosUnidos: 'Sus extremos están unidos.',
    extremosSueltos: (n: number) =>
      n === 1
        ? '1 extremo suelto (anillo ámbar en el plano).'
        : `${n} extremos sueltos (anillos ámbar en el plano).`,
    codigo: (c: string) => `Código en la simulación: ${c}`,
    fueraDelEspacio: 'Fuera del espacio: no se simula.',
    sobresale: 'Sobresale del espacio: en el terreno real no cabría.',
  },
  exportarPlanta: {
    boton: 'Exportar planta',
    botonAyuda: 'Planta acotada del espacio, en PDF (para imprimir a escala) o como imagen.',
    titulo: 'Exportar planta acotada',
    ayuda: (espacio: string) =>
      `Planta de «${espacio}» con sus cotas, una cuadrícula de referencia, las áreas y la leyenda, a la escala normalizada más grande que entra en la hoja.`,
    hoja: 'Hoja',
    formato: 'Formato',
    pdf: 'PDF',
    png: 'Imagen PNG',
    pdfAyuda: 'Vectorial: se imprime a escala y se mide sobre el papel.',
    espacios: 'Espacios',
    soloActivo: 'Solo el activo',
    todos: (n: number) => `Todos (${n} páginas)`,
    pngAyuda: 'Imagen de unos 150 puntos por pulgada, para presentaciones o correos.',
    escala: (n: number, papel: string, horizontal: boolean) =>
      `Escala 1:${n} · ${papel} ${horizontal ? 'horizontal' : 'vertical'}`,
    descargar: 'Descargar',
    exportada: (archivo: string) => `Planta exportada: ${archivo}`,
    error: (detalle: string) => `No se pudo exportar la planta: ${detalle}`,
    areaPiso: (acabado: string, m2: string) => `${acabado}: ${m2} m²`,
    pie: (version: string) => `Gemba Studio ${version} · datos ficticios o del usuario`,
  },
  grupo: {
    titulo: 'Posición y medidas del grupo',
    bordeIzquierdo: 'Borde izquierdo x (m)',
    bordeSuperior: 'Borde superior z (m)',
    giro: 'Giro de cada pieza (°)',
    varia: 'varía',
    tipo: (nombre: string, n: number) => `${nombre} × ${n}`,
    medidaGrupo: (ancho: string, fondo: string) => `El grupo mide ${ancho} × ${fondo} m.`,
  },
  capas: {
    titulo: 'Capas',
    ayuda:
      'Ocultar una capa la saca del plano y de la selección; bloquearla la deja a la vista sin que se pueda tocar. Solo en Editar: la simulación usa todo el espacio. Se recuerda en este navegador.',
    capa: 'Capa',
    ver: 'Ver',
    bloquear: 'Bloquear',
    verCapa: (n: string) => `Ver la capa ${n}`,
    bloquearCapa: (n: string) => `Bloquear la capa ${n}`,
    nombres: {
      espacios: 'Espacios (marcos)',
      estructura: 'Estructura',
      operacion: 'Operación',
      circulacion: 'Circulación',
    },
  },
  rejilla: {
    ver: 'Rejilla',
    ayuda:
      'Muestra en el espacio líneas cada 1 m y, más marcadas, cada 5 m (se recuerda en este navegador).',
    cuadricula: 'Cuadrícula',
    cuadriculaAyuda:
      'Paso de ajuste al colocar, arrastrar, dibujar, pegar y con las manijas (se recuerda en este navegador). Alt deja el punto libre o cada 0.1 m.',
  },
  mano: {
    boton: 'Mano',
    ayuda:
      'Mano (H): arrastra para desplazar la vista sin seleccionar ni mover nada. También mientras mantienes la barra espaciadora, o con el botón central del mouse.',
  },
  atajos: {
    boton: 'Atajos',
    ayuda: 'Lista de atajos de teclado (tecla ?).',
    titulo: 'Atajos de teclado',
    lista: [
      ['Clic · Mayús+clic', 'Seleccionar · sumar o quitar de la selección'],
      ['Mayús+arrastrar en vacío', 'Seleccionar por área'],
      ['Arrastrar el fondo · botón central', 'Desplazar la vista'],
      ['H · barra espaciadora (mantenida)', 'Mano: desplazar sin mover nada'],
      ['Rueda · botón derecho', 'Acercar o alejar · girar la vista (isométrica)'],
      ['Ctrl+C · Ctrl+X · Ctrl+V', 'Copiar · cortar · pegar (en el cursor o al lado)'],
      ['Ctrl+D', 'Duplicar al lado'],
      ['Ctrl+A', 'Seleccionar todo el espacio activo'],
      ['Flechas · Mayús+flechas', 'Mover 0.1 m · 1 m'],
      ['R · Mayús+R', 'Girar 90° · 45° (también el elemento por colocar)'],
      ['Mayús+clic al colocar', 'Colocar y seguir colocando'],
      ['Mayús al dibujar', 'Ángulos de 45°'],
      ['Mayús al arrastrar', 'Bloquear el eje'],
      ['Alt', 'Soltar el imán y las guías; cuadrícula de 0.1 m en las manijas'],
      ['Enter · doble clic', 'Terminar una vía, un muro o una zona'],
      ['Supr · Retroceso', 'Eliminar (o borrar el punto seleccionado)'],
      ['M', 'Medir'],
      ['B', 'Mostrar u ocultar el catálogo'],
      ['Espacio (en Simular)', 'Reproducir o pausar'],
      ['Esc', 'Cancelar la herramienta o soltar la selección'],
      ['Ctrl+Z · Ctrl+Y', 'Deshacer · rehacer'],
      ['?', 'Esta lista'],
    ] as readonly (readonly [string, string])[],
    cerrar: 'Cerrar',
  },
  indicaciones: {
    seleccionar:
      'Clic selecciona · Mayús+clic suma · Mayús+arrastrar en vacío selecciona un área · arrastrar el fondo mueve la vista',
    uno: 'Arrastra para mover · manijas: medidas y giro · R gira · Supr elimina · Esc suelta',
    forma:
      'Arrastra para moverlo · manijas: puntos (la «+» agrega uno) · Supr borra el punto seleccionado',
    varios: (n: number) =>
      `${n} seleccionados: arrastra uno para mover el grupo · alinear y medidas en Propiedades`,
    colocar: (nombre: string) =>
      `Clic para colocar «${nombre}» · Mayús+clic coloca y sigue · R gira 90° · Esc termina`,
    via: 'Clic agrega puntos · al acercarte a otra vía se une sola (imán; Alt lo suelta) · Mayús: 45° · Enter o doble clic termina',
    muro: 'Clic en cada esquina del muro · Mayús: 45° · Enter o doble clic termina · Esc cancela',
    zona: 'Clic en cada esquina de la zona · Mayús: 45° · Enter o doble clic la cierra · Esc cancela',
    medir: 'Clic en dos puntos para medir · un tercero empieza otra medida · Esc termina',
    mano: 'Arrastra para desplazar la vista · H o Esc vuelve a seleccionar',
  },
  espacios: {
    titulo: (n: number) => `Espacios de la pizarra (${n})`,
    ayuda:
      'Cada espacio es una operación con su propio marco, layout y parámetros. Haz clic en uno (aquí o en el plano) para editarlo; se simula el que está activo.',
    activo: 'Editando',
    medidas: (ancho: string, fondo: string) => `${ancho} × ${fondo} m`,
    nuevoBlanco: 'Nuevo en blanco',
    nuevoTallerBase: 'Nuevo desde el Taller base',
    duplicar: 'Duplicar',
    duplicarAyuda:
      'Copia este espacio con todo su contenido y su operación, a la derecha de los demás (para armar el escenario futuro).',
    girar: 'Girar 90°',
    girarAyuda:
      'Gira el espacio y todo su contenido 90° en el sentido de las agujas del reloj, alrededor de su centro.',
    eliminar: 'Eliminar espacio',
    eliminarTitulo: (nombre: string) => `¿Eliminar «${nombre}»?`,
    eliminarTexto:
      'Se elimina el espacio con todo su contenido y su operación. Puedes deshacerlo con Ctrl+Z.',
    soloUno: 'El proyecto necesita al menos un espacio.',
    copiarDe: 'Copiar parámetros de…',
    copiarAyuda:
      'Copia de otro espacio la operación: parámetros, servicios, etapas y rutas, políticas, semáforo y simulación. El layout y las medidas no cambian.',
    copiarElegir: 'Elegir espacio',
    verTodo: 'Ver todo',
    verTodoAyuda: 'Encuadra todos los espacios de la pizarra.',
    mover: 'Arrastra la manija con la cruz, debajo del espacio, para moverlo entero.',
    contener: 'Crear un espacio que los contenga',
    contenerAyuda:
      'Lleva lo que quedó fuera del marco a un espacio nuevo que lo contiene, con la misma operación.',
    simulaActivo: (nombre: string) =>
      `Se simula el espacio activo: «${nombre}». Para simular otro, haz clic en él.`,
    nombreNuevo: (n: number) => `Espacio ${n}`,
    nombreTallerBase: 'Taller base',
    nombreCopia: (nombre: string) => `${nombre} · copia`,
    comandoNuevo: 'Agregar espacio',
    comandoDuplicar: 'Duplicar espacio',
    bloquean: (n: number) => `${n} ${n === 1 ? 'bloquea' : 'bloquean'}`,
  },
  espacio: {
    titulo: 'Espacio',
    nombre: 'Nombre del espacio',
    ancho: 'Ancho del espacio (m)',
    fondo: 'Fondo del espacio (m)',
    area: (m2: string) => `Área total: ${m2} m²`,
    ocupada: (m2: string, elementos: string, vias: string) =>
      `Ocupada: ${m2} m² (elementos ${elementos} · vías ${vias})`,
    libre: (m2: string, pct: string) => `Libre: ${m2} m² (${pct} %)`,
    pisos: (lista: string) => `Pisos: ${lista}`,
    areasAyuda:
      'Ocupada: el piso que cubren los elementos sólidos y las vías, contado una vez donde se superponen, dentro del marco. Las marcas pintadas, el ingreso, la salida, el techado, el portón y la barrera no ocupan piso.',
    ayudaMedidas:
      'Con números, la esquina superior izquierda del marco queda fija. Con las manijas del plano (sin nada seleccionado), el borde que arrastras se mueve y el opuesto queda fijo. Los elementos no se mueven.',
    fuera: (n: number) =>
      `${n} ${n === 1 ? 'elemento queda' : 'elementos quedan'} fuera del espacio: no se simulan. Revisa la pestaña Validación.`,
    origenMedidas: {
      ingresado: 'Ingresado',
      perfil: 'Supuesto (del perfil)',
      propuesta: 'Supuesto (propuesta del asistente)',
    } as Record<string, string>,
    pedidas: (ancho: number, fondo: number, respondidas: boolean) =>
      respondidas
        ? `Respondiste ${ancho} × ${fondo} m en el asistente.`
        : `Medida del perfil: ${ancho} × ${fondo} m.`,
    usarPedidas: (ancho: number, fondo: number) => `Usar ${ancho} × ${fondo} m`,
  },
  parametros: {
    titulo: 'Parámetros',
    nota: 'Cada valor indica si es un supuesto (ilustrativo) o un dato ingresado por ti. Al editarlo pasa a «ingresado».',
    demanda: 'Demanda',
    autosPorDia: 'Autos citados por día',
    llegadas: 'Cómo llegan los autos',
    formas: {
      escalonada: 'Escalonados (más en la mañana, repartidos)',
      'pico-manana': 'Pico en la mañana (como el v6)',
      concentrada: 'Muy concentrados de 8 a 10 h',
      pareja: 'Parejos durante el día',
    } as Record<string, string>,
    formaPropia: 'Perfil propio',
    volver: (v: string) => `Volver al supuesto (${v})`,
    volverAyuda: (v: string) =>
      `Vuelve al valor supuesto de la plantilla: ${v}. El dato vuelve a marcarse «Supuesto».`,
    mezcla: (tipo: string) => `% ${tipo}`,
    personas: 'Personas',
    asesores: 'Asesores',
    tecnicos: 'Técnicos',
    tiempos: 'Tiempos de las etapas (min)',
    tiempoPropio: (servicio: string, t: string) => `${servicio}: ${t} (propio, en «Servicios»)`,
    min: 'Mín.',
    tipico: 'Típico',
    max: 'Máx.',
    constante: 'Constante',
    jornada: 'Jornada',
    inicioTurno: 'Inicio de turno',
    finTurno: 'Fin de turno',
    ultimaCita: 'Última cita',
    cierre: 'Cierre del día',
    promesas: 'Promesas',
    formaPromesa: (tipo: string) => `Cómo se promete ${tipo}`,
    formasPromesa: {
      habiles: 'Plazo en horas hábiles',
      hoy: 'Hoy a una hora fija',
      manana: 'Al día siguiente a una hora fija',
    } as Record<string, string>,
    plazo: (tipo: string) => `Plazo ${tipo} (h hábiles)`,
    horaPromesa: (tipo: string) => `Hora prometida ${tipo}`,
    horaPromesaAyuda:
      'Hoy a una hora fija: si la OT se abre después de esa hora, se promete para el día siguiente a la misma hora.',
    horas: (h: string) => `${h} h`,
    refrigerio: 'Refrigerio de los técnicos',
    sinRefrigerio: 'Sin refrigerio.',
    agregarRefrigerio: 'Agregar refrigerio',
    quitarRefrigerio: 'Quitar refrigerio',
    inicioRefrigerio: 'Inicio del refrigerio',
    duracionRefrigerio: 'Duración del refrigerio (min)',
    modoRefrigerio: 'Cómo paran',
    modosRefrigerio: {
      todos: 'Todos a la vez',
      escalonado: 'Escalonados, en dos grupos',
    } as Record<string, string>,
    refrigerioAyuda:
      'Ningún técnico empieza trabajo nuevo durante su refrigerio; si a esa hora está con un auto, sale al terminar con él y descansa completo. Escalonados: T01, T03… a la hora de inicio y T02, T04… cuando vuelve el primer grupo.',
    redondeo: 'Redondeo de la hora prometida (min)',
    movimiento: 'Movimiento',
    velocidad: 'Velocidad de los autos (km/h)',
    maniobra: 'Maniobra por traslado (s)',
    trasladoFijo: 'Traslado fijo (min)',
    trasladoFijoNota:
      'Este caso usa un tiempo fijo por traslado (parámetro de prueba), no la distancia.',
    politicas: 'Políticas de operación',
    lavadoDespuesDeHora: 'El lavado termina los autos del día',
    lavadoDespuesDeHoraAyuda: (fin: string, cierre: string) =>
      `Después del fin del turno (${fin}) el lavado sigue recibiendo autos hasta el cierre (${cierre}), como los técnicos que terminan en horas extra. Si se desmarca, no empieza lavados después de las ${fin}.`,
    origen: {
      supuesto: 'Supuesto',
      ingresado: 'Ingresado',
      importado: 'Importado',
      calibrado: 'Calibrado',
    } as Record<string, string>,
  },
  paneles: {
    panel: 'Panel de propiedades',
    ocultar: (n: string) => `Ocultar ${n.toLowerCase()}`,
    abrir: (n: string) => `Mostrar ${n.toLowerCase()}`,
  },
  capacidad: {
    titulo: 'Capacidad estimada',
    estimacion: 'Estimación',
    recurso: (nombre: string, n: number) =>
      `${nombre.charAt(0).toUpperCase()}${nombre.slice(1)} (${n})`,
    sinUnidades: 'No hay',
    horas: (req: string, disp: string) => `${req} h pedidas de ${disp} h`,
    caben: (n: number, demanda: number) =>
      n >= demanda
        ? `Con esta mezcla caben ≈ ${n} autos al día; hoy se citan ${demanda}.`
        : `Con esta mezcla caben ≈ ${n} autos al día y se citan ${demanda}: el resto quedará sin atender o pendiente.`,
    nota: 'Cálculo con los tiempos promedio, sin colas ni variabilidad: avisa si la demanda supera claramente lo que el taller puede atender. El resultado lo da la simulación.',
    advertencia: 'Advertencia de capacidad',
  },
  validacion: {
    titulo: 'Validación',
    ok: 'Sin observaciones: el espacio está listo para simular.',
    bloquean: (n: number) => `${n} ${n === 1 ? 'bloquea' : 'bloquean'} la simulación`,
    advierten: (n: number) => `${n} ${n === 1 ? 'advertencia' : 'advertencias'}`,
    bloquea: 'Bloquea',
    advierte: 'Advierte',
    donde: 'Dónde',
    porque: 'Por qué importa',
    queHacer: 'Qué hacer',
    verEnPlano: 'Ver en el plano',
  },
  simulacion: {
    titulo: 'Simulación',
    simular: 'Simular',
    simularDe: (n: number) => `Simular ${n} réplicas`,
    calculando: (h: number, t: number) => `Calculando réplica ${h} de ${t}…`,
    calculandoAuto: (h: number, t: number) =>
      `Calculando réplica ${h} de ≈ ${t} (hasta lograr la precisión)…`,
    cancelar: 'Cancelar',
    cancelada: 'Simulación cancelada.',
    replicas: 'Réplicas',
    replicasAutomaticas: 'Réplicas automáticas',
    replicasAutomaticasAyuda:
      'Empieza con 20 y agrega réplicas hasta que los indicadores alcanzan la precisión pedida (máximo 200). Desmárcalo para fijar el número.',
    semilla: 'Semilla',
    horizonte: 'Horizonte: 1 jornada',
    bloqueada: 'Corrige las observaciones que bloquean antes de simular.',
    verValidacion: 'Ver validación',
    sinResultados:
      'Aún no hay resultados. Pulsa «Simular»: el motor calcula la jornada en segundo plano y luego la verás en 3D.',
    desactualizada:
      'Cambiaste el espacio después de simular: estos resultados corresponden a la configuración anterior. Vuelve a simular.',
    escenaDesactualizada:
      'El espacio cambió después de simular: la jornada anterior ya no se reproduce. Vuelve a simular para verla con el espacio actual.',
    resultado: 'Resultado calculado',
    resumen: (n: number, s: number, ms: string, auto: boolean) =>
      `${n} réplicas${auto ? ' automáticas' : ''} · semilla ${s} · ${ms}`,
    ic: (h: string) => `± ${h} (IC 95 %)`,
    sinDato: 'Sin dato',
    insuficiente: (n: number) =>
      `Las réplicas no alcanzan para concluir con esa precisión; se recomiendan ≈ ${n} réplicas. Con «Réplicas automáticas» el motor las agrega solo.`,
    suficiente: 'Precisión suficiente con estas réplicas.',
    suficienteAuto: (n: number) =>
      `Precisión alcanzada con ${n} réplicas: todos los indicadores principales cumplen la precisión pedida.`,
    maximo: (n: number) =>
      `Se llegó al máximo de ${n} réplicas sin lograr la precisión pedida en todos los indicadores: lee los intervalos con cuidado.`,
    porTiempo: (n: number) =>
      `El cálculo se detuvo por tiempo con ${n} réplicas, antes de lograr la precisión pedida: lee los intervalos con cuidado.`,
    composicion: 'Composición del lead time a listo',
    pendientes: 'Trabajo pendiente al cierre (promedio por réplica)',
    sinPendientes: 'Ningún auto quedó con trabajo pendiente al cierre.',
    listosSinRetirar: 'Aparte: listos esperando que el cliente los retire',
    fallo: (r: number) => `La réplica ${r} se detuvo y no se muestran resultados`,
    supuestos:
      'Resultados del motor con supuestos ilustrativos de un taller ficticio. No son mediciones. Calibración: pendiente por falta de datos.',
    limitaciones:
      'Limitaciones de esta versión: los autos no se ceden el paso (los traslados no interactúan), no hay torre de control ni recorridos a pie, y se simula una jornada.',
    llegaron: (n: string) => `de ${n} que llegaron`,
    detalle: 'Más indicadores',
    formula: 'Fórmula',
  },
  monitor: {
    titulo: (espacio: string) => `Monitor · ${espacio}`,
    etiqueta: (estado: string) => `Monitor · ${estado}`,
    sinSimular: 'Sin simular',
    calculando: 'Calculando…',
    simulado: 'Jornada simulada',
    desactualizado: 'Desactualizado',
    errores: (n: number) => `${n} ${n === 1 ? 'observación bloquea' : 'observaciones bloquean'}`,
    recursos: (elevadores: number, tecnicos: number) => `${elevadores} elev · ${tecnicos} téc`,
    mover: 'Arrastra la cabecera para mover el monitor',
    encuadrar: 'Encuadrar el espacio y su monitor',
    minimizar: 'Minimizar el monitor',
    expandir: 'Expandir el monitor',
    tablero: 'Tablero',
    leadTime: 'Lead time',
    esperando: 'Calculando la jornada en segundo plano…',
    ahora: (n: number) => `Ahora: ${n} ${n === 1 ? 'auto' : 'autos'} en el taller`,
    dondeVer: 'Los resultados están en el monitor, junto al espacio.',
    verMonitor: 'Ver el monitor',
  },
  tiempo: {
    reproducir: 'Reproducir',
    pausar: 'Pausar',
    siguienteEvento: 'Siguiente evento',
    irAlCierre: 'Ir al cierre',
    reiniciar: 'Volver al inicio del día',
    velocidad: 'Velocidad',
    reloj: 'Hora simulada',
    autos: (n: number) => `${n} ${n === 1 ? 'auto' : 'autos'} en el taller`,
  },
  balizas: {
    pisoElevador: 'El piso de cada elevador ocupado toma el color de la baliza de su auto.',
    // Los nombres de cada estado son datos del semáforo (nucleo/dominio/semaforo.ts).
    titulo: 'Balizas',
    apagadas: 'Balizas apagadas',
  },
  semaforo: {
    seccion: 'Semáforo (balizas)',
    titulo: 'Semáforo de estados (balizas)',
    intro:
      'Cada estado tiene nombre y color, y agrupa etapas del proceso. Las balizas ayudan a ver el flujo; no cambian los resultados del motor.',
    activo: 'Mostrar balizas sobre los autos',
    estados: 'Estados',
    nombre: 'Nombre del estado',
    color: (n: string) => `Color de «${n}»`,
    colores: 'Colores sugeridos',
    usarColor: (c: string) => `Usar el color ${c}`,
    regla: 'Cuándo se usa',
    porEtapas: 'Por etapas (tabla de abajo)',
    reglas: {
      atraso: 'Atrasado frente a la promesa',
      listo: 'Listo para entregar',
    } as Record<string, string>,
    parpadea: 'Parpadea',
    parpadeaAyuda: 'Alterna entre su color y apagado, para llamar la atención.',
    subir: (n: string) => `Subir «${n}» en la leyenda`,
    bajar: (n: string) => `Bajar «${n}» en la leyenda`,
    quitar: (n: string) => `Quitar «${n}»`,
    agregar: 'Agregar estado',
    nuevo: 'Nuevo estado',
    etapas: 'Qué estado muestra cada etapa',
    etapasAyuda:
      'Para cada etapa: mientras el auto la espera o va hacia ella, mientras está en curso y, si tiene interrupción (por ejemplo, la espera de repuesto), mientras está interrumpida.',
    etapa: 'Etapa',
    fases: {
      espera: 'Esperando o en camino',
      curso: 'En curso',
      interrumpida: 'Interrumpida',
    } as Record<string, string>,
    celda: (etapa: string, fase: string) => `${etapa}: ${fase.toLowerCase()}`,
    sinBaliza: 'Sin baliza',
    reglasAyuda:
      'Los estados con regla tienen prioridad sobre la tabla: «Atrasado» se usa cuando la OT superó su hora prometida y el auto aún no está listo; «Listo», desde que el auto queda listo para entregar.',
    vistaPrevia: 'Vista previa de la leyenda',
    apagado: 'Balizas apagadas: los autos no muestran estado.',
    restablecer: 'Restablecer valores por defecto',
    guardar: 'Guardar semáforo',
    editar: 'Editar semáforo…',
    propio: 'Semáforo propio de este espacio.',
    porDefecto: 'Semáforo por defecto (los 7 estados acordados).',
    sinEstados: 'Debe haber al menos un estado.',
    sinNombre: 'Cada estado necesita un nombre.',
    colorInvalido: 'Hay un color con formato inválido (usa #rrggbb).',
    reglaRepetida: (r: string) => `Solo un estado puede usar la regla «${r}».`,
  },
  asistente: {
    titulo: 'Configura tu taller',
    subtitulo:
      'Unas preguntas cortas para armar un taller que ya valida y simula. Todo se puede editar después.',
    paso: (n: number, total: number) => `Paso ${n} de ${total}`,
    salir: 'Salir',
    atras: 'Atrás',
    siguiente: 'Siguiente',
    noSe: 'No sé, usar el del perfil',
    empezar: 'Empezar',
    lienzoEnBlanco: 'Prefiero un lienzo en blanco',
    perfilPregunta: '¿Qué tipo de taller se parece más al tuyo?',
    perfilAyuda:
      'El perfil da valores de partida creíbles a todo lo que no se pregunta. Son supuestos ilustrativos, no mediciones.',
    resumenPerfil: (
      ancho: number,
      fondo: number,
      autos: number,
      elevadores: number,
      tecnicos: number,
    ) =>
      `${ancho} × ${fondo} m · ${autos} autos al día · ${elevadores} elevadores · ${tecnicos} técnicos`,
    preguntas: {
      tamano: {
        titulo: '¿Cuánto mide el terreno o la nave del taller?',
        ayuda:
          'Mide el área que usa la operación: ingreso, recepción, elevadores, patio, lavado y entrega. Si los elementos no caben, el resumen propone una medida.',
      },
      horario: {
        titulo: '¿A qué hora abre y cierra el taller, y hasta qué hora se reciben autos?',
        ayuda:
          'El cierre del día queda 1 h 30 min después del fin de la jornada (supuesto) y la promesa en horas hábiles usa este horario.',
      },
      demanda: {
        titulo: '¿Cuántos autos atienden en un día normal, y cómo llegan?',
        ayuda: 'Los autos se reparten entre la apertura y la última cita según cómo llegan.',
      },
      servicios: {
        titulo: '¿Qué servicios hacen y qué parte de los autos va a cada uno?',
        ayuda:
          'La suma da 100 %: al cambiar uno, el resto se reparte entre los demás. Un servicio nuevo copia el recorrido y los tiempos del que elijas y queda «Supuesto».',
      },
      promesa: {
        titulo: '¿Cuándo prometen entregar cada servicio?',
        ayuda:
          'En horas hábiles desde la apertura de la OT, hoy a una hora fija (si la OT se abre después, al día siguiente a esa hora) o al día siguiente a una hora fija.',
      },
      elevadores: {
        titulo: '¿Cuántos elevadores tienen y de qué tipo? ¿Cuántos puestos de lavado?',
        ayuda:
          'Los cajones de recepción, patio y entrega salen del perfil, en proporción a la demanda (supuesto).',
      },
      personas: {
        titulo: '¿Cuántas personas atienden?',
        ayuda:
          'El controlador de torre y el jefe de taller se preguntarán cuando el motor los simule (paso 1.6).',
      },
      flujo: {
        titulo: '¿Cómo es el recorrido de un auto en tu taller?',
        ayuda:
          'Cambia las etapas y rutas del proceso. Todo se puede ajustar después en «Etapas y rutas».',
      },
    } as Record<string, { titulo: string; ayuda: string }>,
    nombres: {
      tamano: 'Tamaño',
      horario: 'Horario',
      demanda: 'Demanda',
      servicios: 'Servicios y mezcla',
      promesa: 'Promesa',
      elevadores: 'Elevadores y lavado',
      personas: 'Personas',
      flujo: 'Flujo de trabajo',
    } as Record<string, string>,
    ancho: 'Ancho (m)',
    fondo: 'Fondo (m)',
    apertura: 'Apertura',
    finJornada: 'Fin de la jornada de los técnicos',
    ultimaCita: 'Última cita o último auto que se recibe',
    hayRefrigerio: 'Los técnicos tienen refrigerio',
    inicioRefrigerio: 'Inicio del refrigerio',
    duracionRefrigerio: 'Duración del refrigerio (min)',
    modoRefrigerio: '¿Paran todos a la vez o escalonados?',
    refrigerioAyuda:
      'Ningún técnico empieza trabajo nuevo en su refrigerio; si está con un auto, sale al terminar. Escalonados: la mitad a la hora de inicio y la otra mitad cuando vuelve la primera.',
    autos: 'Autos por día',
    comoLlegan: 'Cómo llegan',
    llegadas: {
      escalonada: 'Escalonados: más en la mañana, pero repartidos',
      'pico-manana': 'Pico en la mañana: casi la mitad antes de las 10:00',
      pareja: 'Parejos durante el día',
    } as Record<string, string>,
    nombreServicio: (i: number) => `Servicio ${i}`,
    parte: (n: string) => `% de los autos: ${n}`,
    quitar: (n: string) => `Quitar «${n}»`,
    agregarServicio: 'Agregar servicio',
    nuevoServicio: 'Servicio nuevo',
    parecidoA: 'Recorrido y tiempos parecidos a',
    suma: (v: string) => `Suma: ${v} %`,
    formaPromesa: (n: string) => `Cómo se promete «${n}»`,
    horasPromesa: (n: string) => `Plazo de «${n}» (h hábiles)`,
    horaPromesa: (n: string) => `Hora prometida de «${n}»`,
    fastLane: (servicios: string) => `Fast Lane (solo ${servicios})`,
    mixtos: 'Mixtos (todo servicio)',
    mayores: (servicios: string) => `Mayores (solo ${servicios})`,
    lavados: 'Puestos de lavado (0 si no lavan)',
    sinElevadores: 'Pon al menos un elevador.',
    tecnicos: 'Técnicos',
    asesores: 'Asesores de servicio',
    tecnicoPorElevador: '¿Cada técnico trabaja en un elevador fijo o toma el que esté libre?',
    tecnicosPorElevador: {
      libre: 'Toma el que esté libre',
      fijo: 'Trabaja en un elevador fijo',
    } as Record<string, string>,
    tecnicoPorElevadorNota:
      'Se simula desde la 1.6: por ahora se guarda como dato ingresado y no cambia los resultados.',
    seLava: '¿Se lava el auto antes de entregarlo?',
    control: '¿Quién hace el control de calidad?',
    controlNota: 'Jefe de taller o inspector: paso 1.6.',
    diagnostico:
      '¿Las reparaciones pasan por diagnóstico y aprobación del cliente antes de empezar?',
    decide: 'Mientras el cliente decide, ¿el auto se queda en el elevador o espera en el patio?',
    si: 'Sí',
    no: 'No',
    controles: {
      'mismo-tecnico': 'El mismo técnico',
      'otro-tecnico': 'Otro técnico',
      asesor: 'El asesor (revisión administrativa)',
      'no-se-hace': 'No se hace',
    } as Record<string, string>,
    decisiones: {
      elevador: 'Se queda en el elevador',
      patio: 'Espera en el patio',
    } as Record<string, string>,
    resumen: 'Resumen',
    resumenTitulo: 'Tu taller propuesto',
    plano: 'Layout propuesto, en planta',
    planoAyuda:
      'Cada elemento ya mira a su vía; el ingreso queda al este. Todo se puede mover después en el editor.',
    medidas: (ancho: number, fondo: number) => `${ancho} × ${fondo} m`,
    noCabe: (ancho: number, fondo: number, pa: number, pf: number) =>
      `No cabe en ${ancho} × ${fondo} m: el layout propuesto necesita ${pa} × ${pf} m. ¿Qué medida usamos?`,
    usarPropuesta: (ancho: number, fondo: number) => `Usar ${ancho} × ${fondo} m`,
    usarPropuestaAyuda:
      'El espacio crece hasta la medida propuesta, que queda como «Supuesto (propuesta del asistente)». La que respondiste se sigue viendo en Propiedades.',
    mantener: (ancho: number, fondo: number) => `Mantener ${ancho} × ${fondo} m`,
    mantenerAyuda:
      'El taller se crea con tus medidas. Lo que no cabe queda marcado en la validación para reacomodarlo en el editor.',
    eligeMedidas: 'Elige qué medida usar para crear el taller.',
    noCaben: (n: number, ancho: number, fondo: number) =>
      `${n} ${n === 1 ? 'elemento no cabe' : 'elementos no caben'} en ${ancho} × ${fondo} m: ${n === 1 ? 'queda marcado' : 'quedan marcados'} en el plano y en la validación.`,
    supuestoPropuesta: 'Supuesto (propuesta del asistente)',
    porDecidir: 'Por decidir',
    respondiste: (ancho: number, fondo: number) => `Respondiste ${ancho} × ${fondo} m`,
    delPerfil: (ancho: number, fondo: number) => `Medida del perfil: ${ancho} × ${fondo} m`,
    valida: 'El modelo valida: está listo para simular.',
    bloquean: (n: number) =>
      `${n} ${n === 1 ? 'observación bloquea' : 'observaciones bloquean'} la simulación:`,
    origen: 'Lo que respondiste y lo que viene del perfil',
    ingresado: 'Ingresado',
    supuesto: 'Supuesto (del perfil)',
    noPregunta:
      'Lo que no se pregunta queda «Supuesto» del perfil y se edita en Parámetros: tiempos de cada etapa, probabilidades de repuesto y aprobación, horas vendidas, retiro del cliente, velocidad de traslado y redondeo de la promesa.',
    tiempo: (min: number, s: number) => `Tiempo en el asistente: ${min} min ${s} s`,
    crear: 'Crear mi taller',
    leyenda: {
      'fast-lane': 'Elevador Fast Lane',
      mixto: 'Elevador mixto',
      mayor: 'Elevador mayor',
      lavado: 'Lavado',
      recepcion: 'Recepción',
      patio: 'Patio',
      entrega: 'Entrega',
      oficina: 'Oficina de asesores',
      via: 'Vía',
      pedidas: 'Tus medidas',
      'no-cabe': 'No cabe',
    } as Record<string, string>,
  },
  servicios: {
    seccion: 'Servicios y mezcla',
    mezclaNota: 'Parte de los autos del día. Al cambiar uno, el resto se reparte entre los demás.',
    editar: 'Editar servicios…',
    titulo: 'Servicios del taller',
    intro:
      'Cada servicio tiene su parte de los autos del día, su promesa, sus horas vendidas, las probabilidades que deciden su recorrido, los elevadores donde se atiende y sus tiempos. Las etapas y rutas se editan en el paso siguiente.',
    lista: 'Servicios',
    nombre: 'Nombre del servicio',
    parte: 'Parte de los autos del día (%)',
    formaPromesa: 'Cómo se promete',
    promesa: 'Plazo (h hábiles desde la apertura de la OT)',
    horaPromesa: 'Hora prometida',
    horasVendidas: 'Horas vendidas por auto (h)',
    probabilidades: 'Probabilidades que deciden su recorrido (%)',
    sinProbabilidades: 'Su recorrido no depende de probabilidades.',
    elevadores: 'Elevadores donde se atiende',
    elevadoresAyuda:
      'Un elevador especializado (Fast Lane, Mayor) se prefiere para los servicios que lo admiten.',
    noUsaElevador: 'Este servicio no pasa por elevadores.',
    recorrido: 'Etapas por las que puede pasar',
    tiempos: 'Tiempos de este servicio (min)',
    tiemposAyuda:
      'Al editar un tiempo, queda propio de este servicio; los demás siguen con el común.',
    comun: 'Común',
    propio: 'Propio',
    usarComun: 'Usar el común',
    agregar: 'Agregar servicio',
    nuevoNombre: 'Nombre del servicio nuevo',
    aPartirDe: 'Copiar recorrido y valores de',
    agregarAyuda:
      'El servicio nuevo sigue el recorrido del que elijas y entra con 0 % de los autos; luego ajusta sus valores.',
    confirmarAgregar: 'Agregar',
    quitar: (n: string) => `Quitar «${n}»`,
    confirmarQuitar: (n: string) =>
      `¿Quitar «${n}»? Su parte de los autos se reparte entre los demás.`,
    etapasQueSeQuitan: (lista: string) =>
      `También se quitan las etapas que solo usaba este servicio: ${lista}.`,
    siQuitar: 'Sí, quitar',
    unoAlMenos: 'El taller necesita al menos un servicio.',
    cancelar: 'Cancelar',
    guardar: 'Guardar servicios',
    sinNombre: 'Cada servicio necesita un nombre.',
    nombreRepetido: (n: string) => `Hay dos servicios llamados «${n}».`,
    sinElevador: (n: string) => `Marca al menos un tipo de elevador para «${n}».`,
    tiempoIncoherente: (s: string, e: string) => `Tiempos incoherentes en «${e}» (${s}).`,
    atiende: (lista: string) => (lista ? `Atiende: ${lista}.` : 'No atiende ningún servicio.'),
    atiendeAyuda: 'Qué servicios atiende cada tipo de elevador se elige en «Servicios».',
    porcentaje: (v: string) => `${v} %`,
  },
  politicas: {
    nota: 'Reglas del taller que cambian los resultados. Se eligen sin programar.',
    estacion: 'Qué elevador se asigna',
    estaciones: {
      'mas-cercana': 'El más cercano por la ruta',
      'menor-codigo': 'El de menor código (B01, B02…)',
    } as Record<string, string>,
    estacionAyuda:
      'Siempre entre los libres que atienden el servicio, prefiriendo el especializado (Fast Lane, Mayor).',
    persona: 'Qué técnico se asigna',
    personas: {
      'menor-carga': 'El de menor carga del día',
      'menor-codigo': 'El de menor código (T01, T02…)',
    } as Record<string, string>,
    personaAyuda: 'Entre los técnicos libres en su turno.',
    colas: 'Quién va primero en las colas',
    ordenes: {
      llegada: 'Por orden de llegada',
      promesa: 'Primero la promesa más cercana',
    } as Record<string, string>,
    colasAyuda:
      'Cuando varios autos esperan lo mismo (elevador, técnico, lavado). Los autos aún sin OT abierta siguen por orden de llegada.',
    decision: (n: string) => `Dónde espera el auto en «${n}»`,
    decisiones: {
      elevador: 'En el elevador (lo retiene)',
      patio: 'En el patio (libera el elevador)',
    } as Record<string, string>,
    decisionAyuda:
      'Mientras el cliente decide. En el patio, el elevador queda libre para otro auto y, cuando el cliente decide, el auto vuelve a un elevador compatible; sin cajón de patio libre, espera en el elevador.',
    control: (n: string) => `Quién hace «${n}»`,
    responsables: {
      'mismo-tecnico': 'El mismo técnico del trabajo',
      'otro-tecnico': 'Otro técnico libre',
      asesor: 'El asesor (revisión administrativa)',
      'no-se-hace': 'No se hace',
    } as Record<string, string>,
    controlNuevo: 'Quién hace el control de calidad',
    tecnicoElevador: 'Cada técnico trabaja en',
    tecnicosElevador: {
      libre: 'El elevador que esté libre',
      fijo: 'Un elevador fijo',
    } as Record<string, string>,
    tecnicoElevadorAyuda:
      'Se simula desde la 1.6: por ahora se guarda como dato y no cambia los resultados (el motor asigna el elevador que esté libre).',
    controlAyuda:
      'Con otro técnico o el asesor, el técnico que hizo el trabajo se libera al terminarlo y el auto espera el control en el elevador. Si no se hace, el auto sigue directo a la etapa siguiente.',
    proximas:
      'El jefe de taller o un inspector para el control, el técnico fijo por elevador y el técnico que se libera durante la espera de repuesto se simulan con el motor completo (paso 1.6).',
  },
  proceso: {
    seccion: 'Proceso (etapas y rutas)',
    resumen: (etapas: number, rutas: number, retrabajos: number) =>
      `${etapas} etapas y ${rutas} rutas${retrabajos > 0 ? `, ${retrabajos} ${retrabajos === 1 ? 'retrabajo' : 'retrabajos'}` : ''}.`,
    editar: 'Editar etapas y rutas…',
    titulo: 'Etapas y rutas del proceso',
    intro:
      'Cada auto recorre las etapas siguiendo las rutas: al terminar una etapa toma la primera ruta cuya condición se cumple; «En otro caso» es la ruta por defecto. Un retrabajo es una ruta hacia una etapa anterior, con un máximo de veces por OT.',
    primera: 'Primera etapa',
    etapas: 'Etapas',
    tiposCortos: { 'espera-externa': 'Espera', salida: 'Salida' } as Record<string, string>,
    agregarEtapa: 'Agregar etapa',
    nuevaNombre: 'Nombre de la etapa nueva',
    despuesDe: (n: string) =>
      `Se agrega después de «${n}» y queda en su ruta por defecto: lo que iba de «${n}» a su etapa siguiente pasa por la nueva.`,
    antesDeSalida:
      'Se agrega justo antes de la salida: lo que llegaba a la salida pasa por la nueva.',
    nombre: 'Nombre de la etapa',
    quitarEtapa: (n: string) => `Quitar «${n}»`,
    salidaNota:
      'La salida del taller termina el recorrido: no tiene rutas y no se puede quitar. Se puede renombrar.',
    tipo: 'Tipo de etapa',
    tipos: {
      actividad: 'Actividad (trabajo en el taller)',
      'espera-externa': 'Espera externa (cliente o proveedor)',
    } as Record<string, string>,
    categoria: 'Categoría del tiempo',
    lugar: 'Dónde ocurre',
    lugares: {
      recepcion: 'Cajón de recepción',
      bahia: 'Elevador',
      patio: 'Cajón de patio',
      lavado: 'Puesto de lavado',
      entrega: 'Cajón de entrega',
      actual: 'Donde ya está el auto',
    } as Record<string, string>,
    lugarDeEspera: 'Si no puede empezar, el auto espera',
    esperas: {
      actual: 'Donde está (retiene ese lugar)',
      patio: 'En el patio, si hay cajón libre',
    },
    persona: 'Quién la hace',
    personas: {
      ninguna: 'Nadie (sin personas)',
      asesor: 'Un asesor',
      tecnico: 'Un técnico',
      'tecnico-de-la-unidad': 'El mismo técnico que ya tiene el auto',
    } as Record<string, string>,
    retener: 'La persona sigue con el auto hasta',
    retenerFin: 'El fin de esta etapa',
    hastaEtapa: (n: string) => `El fin de «${n}»`,
    ventana: 'Corre solo en horario hábil',
    desde: 'Desde',
    hasta: 'Hasta',
    obligatoria: 'Obligatoria para',
    hito: 'Al terminar, marca',
    hitos: {
      ninguno: 'Nada',
      'apertura-ot': 'Apertura de la OT',
      'trabajo-terminado': 'Trabajo terminado',
      listo: 'Auto listo para entregar',
    } as Record<string, string>,
    interrupcion: 'Interrupción',
    esperaIntercalada: 'Puede interrumpirse por',
    sinInterrupcion: 'Sin interrupción',
    cuando: 'Cuándo',
    siAtributo: (n: string) => `Si «${n}» (probabilidad por servicio)`,
    conProbabilidad: 'Con una probabilidad',
    momentoDesde: 'Desde (% del trabajo hecho)',
    momentoHasta: 'Hasta (% del trabajo hecho)',
    tiempos: 'Tiempo común (min)',
    sinTiempoComun: 'Esta etapa solo tiene tiempos propios por servicio.',
    tiemposAyuda: 'Los tiempos propios de cada servicio se editan en «Servicios».',
    rutas: 'Rutas al terminar',
    rutasAyuda:
      'Se evalúan en orden y se toma la primera que se cumple. Una ruta con máximo de veces, al agotarse, deja pasar a las siguientes.',
    irA: 'Ir a',
    destino: (i: number) => `Ruta ${i}: ir a`,
    condicion: 'Si',
    condicionDe: (i: number) => `Ruta ${i}: condición`,
    condiciones: {
      servicio: 'El servicio es',
      'atributo-si': 'Se cumple',
      'atributo-no': 'No se cumple',
      probabilidad: 'Con probabilidad',
      sino: 'En otro caso',
    } as Record<string, string>,
    condicionAvanzada: 'Condición avanzada (se conserva)',
    serviciosDe: (i: number) => `Ruta ${i}: servicios`,
    atributoDe: (i: number) => `Ruta ${i}: qué se cumple`,
    probabilidad: 'Probabilidad (%)',
    limite: 'Máximo por OT',
    limiteDe: (i: number) => `Ruta ${i}: máximo de veces por OT`,
    sinLimite: 'Sin límite',
    veces: (n: number) => (n === 1 ? '1 vez' : `${n} veces`),
    retrabajo: 'Retrabajo',
    retrabajoAyuda:
      'Vuelve a una etapa anterior (o a la misma). Necesita un máximo de veces por OT para que el auto siempre termine.',
    subirRuta: (i: number) => `Subir la ruta ${i}`,
    bajarRuta: (i: number) => `Bajar la ruta ${i}`,
    quitarRuta: (i: number) => `Quitar la ruta ${i}`,
    agregarRuta: 'Agregar ruta',
    recorridos: 'Etapas por las que puede pasar cada servicio',
    conObservaciones: (n: number) =>
      `${n} ${n === 1 ? 'observación bloquea' : 'observaciones bloquean'} la simulación. Puedes guardar y seguir después.`,
    yMas: (n: number) => `y ${n} más (ver «Validación» después de guardar).`,
    guardar: 'Guardar proceso',
  },
  categorias: {
    'valor-agregado': 'Valor agregado',
    administrativa: 'Administrativo',
    traslado: 'Traslado',
    espera: 'Espera',
    'espera-cliente': 'Espera del cliente',
  } as Record<string, string>,
  diagnostico: {
    titulo: 'Diagnóstico de rendimiento',
    intro:
      'Mide el cálculo y los cuadros por segundo en este equipo con 3 espacios simultáneos de 38 autos al día. Elige la escena y el nivel de calidad, o mide los tres niveles seguidos. Ejecútalo en tu PC con la GeForce GT 1030 y con la Intel UHD 630, y exporta el archivo de resultados.',
    escena: 'Escena a medir',
    escenas: {
      catalogo: 'Taller Demo con el catálogo completo (la más exigente)',
      referencia: 'Taller Demo de referencia (10 elevadores, con decoración)',
      base: 'Taller base sobrecargado (6 elevadores)',
    } as Record<string, string>,
    automatica: '(automática)',
    medirNiveles: (min: number) => `Medir los tres niveles (≈ ${min} min)`,
    notaTope:
      'La medición dibuja sin tope para conocer la capacidad del equipo. En la aplicación, «Básica» se limita a 30 cuadros por segundo y en pausa no se redibuja.',
    nivel: 'Nivel',
    fpsCorto: 'Cuadros/s (mediana / p5 / promedio)',
    gpu: 'GPU por cuadro',
    llamadas: 'Llamadas · triángulos',
    enPausa: (s: number) => `Cuadros en ${s} s de pausa`,
    noDisponible: 'No disponible',
    verReferencias: 'Ver la escena de referencia en vivo',
    verCatalogo: 'Ver el catálogo completo en la escena de referencia (paso 1.3)',
    iniciar: (s: number) => `Iniciar medición (${s} s)`,
    exportar: 'Exportar resultados',
    avisoVisible: 'Mantén esta ventana visible y no uses la PC hasta que termine.',
    ventanaCompleta:
      'La medición ocupa la ventana completa del navegador, como la escena que se muestra a los clientes. Al terminar vuelve a esta página con los resultados.',
    consejoF11:
      'Para medir a 1920 × 1080, como pide el protocolo (doc. 07), pon el navegador en pantalla completa con F11 antes de empezar.',
    empiezaEn: (s: number) => `La medición empieza en ${s} s`,
    calculandoMedicion: 'Calculando 20 réplicas por espacio…',
    preparandoNivel: (nivel: string, i: number, n: number) =>
      `Preparando «${nivel}» (${i} de ${n})…`,
    midiendoNivel: (nivel: string, i: number, n: number, s: number) =>
      `Midiendo «${nivel}» (${i} de ${n}) · ${s} s`,
    pausaNivel: (nivel: string, i: number, n: number) =>
      `«${nivel}» (${i} de ${n}): contando los cuadros en pausa…`,
    invalida: 'Medición inválida: repetir con la ventana visible',
    calculoInvalido:
      'Medición inválida: la página se ocultó mientras calculaba; repetir con la ventana visible',
    motivos: {
      oculta: 'la página se ocultó',
      hueco: (s: string) => `${s} s seguidos sin cuadros`,
      pocosCuadros: (porcentaje: string, cuadros: string, esperados: string) =>
        `solo ${porcentaje} % de los cuadros esperados (${cuadros} de ${esperados})`,
    },
    lienzo: 'Lienzo medido',
    tareasCalculo: 'Tareas de interfaz > 100 ms durante el cálculo',
    etapa: 'Etapa',
    tareasMayores: 'Tareas > 100 ms',
    tareaMaxima: 'La más larga',
    etapas: {
      calculo: 'Cálculo',
      armado: 'Armado de la escena',
      'cambio-nivel': 'Cambio de nivel',
      medicion: 'Medición y pausa',
      fuera: 'Fuera de las etapas',
    } as Record<string, string>,
    calculo: 'Cálculo de 20 réplicas × 1 jornada',
    porEspacio: 'por espacio',
    fps: 'Cuadros por segundo',
    mediana: 'Mediana',
    p5: 'Percentil 5',
    equipo: 'Equipo detectado',
    tarjeta: 'Tarjeta gráfica',
    navegador: 'Navegador',
    calidad: 'Calidad gráfica',
    criterio: 'Criterio (ADR 0015)',
    noMedido: 'No medido',
    cumple: 'Cumple',
    noCumple: 'No cumple',
    soloReferencia:
      'Solo referencia: este navegador no usa la tarjeta gráfica (render por software).',
    verificacion: 'Casos de verificación del motor (M2–M7)',
    verificacionDesc:
      'Casos pequeños calculados a mano (plan de pruebas, doc. 05). «Verificar ahora» los simula todos y compara con el valor esperado; «Abrir caso» lo crea como proyecto para verlo en la aplicación.',
    verificar: 'Verificar ahora',
    abrirCaso: 'Abrir caso',
    esperado: 'Esperado',
  },
  referencia: {
    titulo: 'Escena de referencia',
    migas: (catalogo: boolean) =>
      catalogo ? 'Escena de referencia · catálogo completo' : 'Escena de referencia',
    taller: 'Taller Demo (ficticio)',
    direccion: '«Maqueta realista»',
    descripcion:
      'Acabado mate y luz cálida; césped y calle que continúan fuera del espacio; sin reflejos (ADR 0018).',
    vistaIso: 'Isométrica',
    vistaPlanta: 'Planta',
    sello:
      'Referencia visual · «Maqueta realista» · Taller Demo ficticio · cifras del motor con supuestos ilustrativos',
    calculando: 'Simulando la jornada de referencia…',
    error: 'No se pudo preparar la escena de referencia.',
    reproducir: 'Reproducir',
    pausar: 'Pausar',
    irAlCierre: 'Ir al cierre',
    monitor: 'Monitor · Taller Demo · día 1',
    espacio: 'Situación actual',
    pestanas: ['Tablero', 'Lead time', 'Parámetros', 'Escenarios'],
    autosListos: 'Autos listos',
    deDemanda: (n: number) => `de ${n}`,
    leadTime: 'Lead time a listo',
    cumplimiento: 'Cumplimiento de promesa',
    ocupacion: 'Ocupación de elevadores',
    productividad: (p: string) => `productividad ${p}`,
    ic: (v: string, n: number) => `± ${v} (IC 95 %, ${n} réplicas)`,
    composicion: 'Composición del lead time a listo',
    ahora: (hora: string) => `En la jornada mostrada, a las ${hora}`,
    enTaller: 'En el taller',
    enElevador: 'En elevador',
    enEspera: 'En espera',
    listos: 'Listos',
    pie: 'Calculado por el motor (20 réplicas) con supuestos ilustrativos del Taller Demo ficticio. No son mediciones.',
    balizas: 'Balizas',
    sinSeleccion: 'Ningún auto en movimiento a esta hora.',
    promesa: (hora: string, aTiempo: boolean) =>
      `Promesa ${hora} · ${aTiempo ? 'a tiempo' : 'atrasada'}`,
    enCurso: 'en curso',
    trasladoA: (destino: string) => `Traslado a ${destino}`,
    esperaDe: (etapa: string) => `Espera · ${etapa}`,
    llegada: 'Llegada y espera de asesor',
    destinos: {
      B: 'elevador',
      R: 'recepción',
      P: 'patio',
      L: 'lavado',
      E: 'entrega',
      S: 'salida',
    } as Record<string, string>,
  },
  calidad: {
    titulo: 'Calidad gráfica',
    alta: 'Alta',
    media: 'Media',
    basica: 'Básica',
    fijar: 'Fijar nivel',
    fijarAyuda:
      'Marcado: la calidad no baja sola aunque la reproducción vaya lenta. Desmarcado: baja un nivel si los cuadros por segundo quedan bajo el mínimo varios segundos.',
    bajo: (nivel: string, fps: number) =>
      `La reproducción iba a ${fps} cuadros por segundo: la calidad bajó a «${nivel}» para mantener la fluidez. Puedes elegir otro nivel y marcar «Fijar nivel».`,
  },
  versiones: {
    boton: 'Versiones',
    botonAyuda: 'Historial de versiones: guarda una versión o vuelve a como estaba el proyecto',
    titulo: 'Historial de versiones',
    explicacion:
      'Gemba Studio guarda una versión al abrir el proyecto, cada 10 minutos de edición y antes de las operaciones de riesgo (eliminar un espacio, copiar los parámetros de otro espacio, restaurar una versión). Se conservan las últimas 50 automáticas y todas las que guardes tú.',
    nombre: 'Nombre de la versión (opcional)',
    nombreEjemplo: 'Ej.: antes de la reunión con el jefe de taller',
    guardar: 'Guardar versión',
    guardada: (n: number) => `Se guardó la versión ${n}.`,
    cargando: 'Leyendo el historial…',
    vacio: 'Todavía no hay versiones. La primera se guarda sola al abrir el proyecto.',
    lista: 'Versiones guardadas',
    numero: (n: number) => `Versión ${n}`,
    motivo: {
      'al-abrir': 'Automática · al abrir el proyecto',
      'cada-10-min': 'Automática · 10 minutos de edición',
      manual: 'Guardada por ti',
      migracion: (esquema: string) =>
        `Original antes de actualizarlo desde el esquema ${esquema} (versión anterior de Gemba Studio)`,
      'eliminar-espacio': (espacio: string) =>
        espacio ? `Antes de eliminar el espacio «${espacio}»` : 'Antes de eliminar un espacio',
      'copiar-parametros': 'Antes de copiar los parámetros de otro espacio',
      restaurar: (n: string) => `Antes de restaurar la versión ${n}`,
      'cargar-otra-pestana': 'Tus cambios antes de cargar los de otra pestaña',
      'reemplazar-otra-pestana': 'Cambios de otra pestaña que se reemplazaron',
      otro: (operacion: string) => `Antes de ${operacion}`,
    },
    resumen: (espacios: number, elementos: number) =>
      `${espacios} ${espacios === 1 ? 'espacio' : 'espacios'} · ${elementos} ${elementos === 1 ? 'elemento' : 'elementos'}`,
    restaurar: 'Restaurar',
    restaurarAyuda: (n: number) => `Restaurar la versión ${n}`,
    restaurarTitulo: (n: number, fecha: string) => `¿Restaurar la versión ${n} (${fecha})?`,
    restaurarTexto:
      'El proyecto vuelve a como estaba en esa versión. Antes se guarda una versión del estado actual («Antes de restaurar»), así puedes volver desde este mismo historial. «Deshacer» empieza de cero.',
    restaurada: (n: number) =>
      `Se restauró la versión ${n}. El estado anterior quedó en el historial como «Antes de restaurar».`,
    noSePuedeRestaurar: 'No se pudo restaurar esa versión.',
    descargar: 'Descargar',
    descargarAyuda: (n: number) => `Descargar la versión ${n} como archivo .gemba.json`,
    eliminar: 'Eliminar',
    eliminarAyuda: (n: number) => `Eliminar la versión ${n}`,
    eliminarTitulo: (n: number) => `¿Eliminar la versión ${n}?`,
    eliminarTexto: 'Solo se borra esta versión del historial; el proyecto no cambia.',
    total: (n: number, tamano: string) =>
      `${n} ${n === 1 ? 'versión' : 'versiones'} · ${tamano} en este navegador`,
    migrado: (esquema: number) =>
      `El proyecto se actualizó desde el esquema ${esquema}. El original quedó en «Versiones», por si necesitas abrirlo con una versión anterior de Gemba Studio.`,
    error: (detalle: string) => `No se pudo guardar la versión: ${detalle}`,
  },
  pestanas: {
    tambienAbierto:
      'Este proyecto también está abierto en otra pestaña. Edítalo en una sola: si cambia en las dos, se te pedirá elegir qué cambios conservar.',
    cambio: 'Cambió en otra pestaña',
    ayuda:
      'Cambió en otra pestaña: este proyecto se guardó desde otra pestaña y aquí se dejó de guardar para no pisar esos cambios. Elige cuáles conservar; los otros quedan en «Versiones».',
    usar: 'Usar el de la otra pestaña',
    conservar: 'Conservar el mío',
    usada:
      'Se abrió lo guardado en la otra pestaña. Tus cambios quedaron en «Versiones» («Tus cambios antes de cargar los de otra pestaña»).',
    conservada:
      'Se guardaron tus cambios. Los de la otra pestaña quedaron en «Versiones» («Cambios de otra pestaña que se reemplazaron»).',
    primeroElegir:
      'Este proyecto cambió en otra pestaña: primero elige qué cambios conservar (en la barra superior).',
  },
  organizacion: {
    lateral: 'Organización de los proyectos',
    espaciosDeTrabajo: 'Espacios de trabajo',
    nuevoEspacio: 'Nuevo espacio de trabajo',
    carpetas: 'Carpetas',
    nuevaCarpeta: 'Nueva carpeta',
    todas: 'Todos los proyectos',
    sinCarpeta: 'Sin carpeta',
    conteo: (n: number) => `${n} ${n === 1 ? 'proyecto' : 'proyectos'}`,
    propio: 'Propio',
    cliente: 'Cliente',
    datosReales: 'Datos reales',
    datosRealesLargo: 'Privado · contiene datos reales',
    datosRealesAyuda:
      'Este espacio de trabajo contiene datos reales de un cliente: la exportación pide confirmación y sus proyectos no salen a un espacio de trabajo sin esta marca. Es organización dentro de este navegador, no seguridad entre clientes.',
    configurar: 'Configurar',
    configurarAyuda: (n: string) => `Configurar el espacio de trabajo «${n}»`,
    editarCarpeta: 'Editar carpeta',
    editarCarpetaAyuda: (n: string) => `Editar la carpeta «${n}»`,
    creaEn: (lugar: string) => `Los proyectos nuevos se crean en «${lugar}».`,
    lugar: (espacio: string, carpeta: string | null) =>
      carpeta ? `${espacio} › ${carpeta}` : espacio,
    buscar: 'Buscar proyectos por nombre',
    buscarEjemplo: 'Buscar por nombre…',
    sinResultados: (q: string) => `Ningún proyecto de este espacio de trabajo se llama «${q}».`,
    resultados: (n: number, total: number) => `${n} de ${total}`,
    carpetaVacia: 'Esta carpeta está vacía.',
    carpetaVaciaSiguiente:
      'Crea un proyecto aquí desde «Empieza aquí», o mueve uno existente con «Opciones».',
    // Diálogo de espacio de trabajo
    espacioTitulo: (nuevo: boolean) => (nuevo ? 'Nuevo espacio de trabajo' : 'Espacio de trabajo'),
    nombre: 'Nombre',
    nombreEspacioEjemplo: 'Ej.: Cliente A · Sede norte',
    tipo: 'Tipo',
    tipoPropio: 'Propio (demos, pruebas, plantillas)',
    tipoCliente: 'Cliente (proyectos de una empresa)',
    marcaDatosReales: 'Contiene datos reales de un cliente',
    marcaDatosRealesAyuda:
      'Márcalo si algún proyecto usa planos, tiempos o nombres reales. La exportación pedirá confirmación y los proyectos no podrán moverse a un espacio de trabajo sin la marca. No convierte a Gemba Studio en un sistema seguro entre clientes: todo vive en este navegador.',
    quitarMarcaTitulo: '¿Quitar la marca «contiene datos reales»?',
    quitarMarcaTexto:
      'Hazlo solo si ningún proyecto de este espacio de trabajo tiene datos reales (por ejemplo, porque ya los anonimizaste).',
    quitarMarca: 'Quitar la marca',
    crearEspacio: 'Crear espacio de trabajo',
    guardar: 'Guardar',
    eliminarEspacio: 'Eliminar espacio de trabajo',
    eliminarEspacioTitulo: (n: string) => `¿Eliminar el espacio de trabajo «${n}»?`,
    eliminarEspacioTexto: 'Se eliminan también sus carpetas. No tiene proyectos.',
    espacioConProyectos:
      'Este espacio de trabajo tiene proyectos: muévelos o elimínalos antes de eliminarlo.',
    espacioPropioFijo: '«Mis proyectos» siempre existe y no se puede eliminar.',
    espacioCreado: (n: string) => `Se creó el espacio de trabajo «${n}».`,
    espacioEliminado: (n: string) => `Se eliminó el espacio de trabajo «${n}».`,
    // Diálogo de carpeta
    carpetaTitulo: (nueva: boolean) => (nueva ? 'Nueva carpeta' : 'Editar carpeta'),
    nombreCarpetaEjemplo: 'Ej.: Demos comerciales',
    color: 'Color',
    colorN: (n: number) => `Color ${n}`,
    crearCarpeta: 'Crear carpeta',
    eliminarCarpeta: 'Eliminar carpeta',
    eliminarCarpetaTitulo: (n: string) => `¿Eliminar la carpeta «${n}»?`,
    eliminarCarpetaTexto: 'Sus proyectos no se borran: quedan «Sin carpeta».',
    carpetaEliminada: (n: string) => `Se eliminó la carpeta «${n}».`,
    // Opciones del proyecto
    opciones: 'Opciones',
    opcionesAyuda: (n: string) => `Opciones de ${n}`,
    opcionesTitulo: 'Opciones del proyecto',
    estadisticas: (espacios: number, elevadores: number, creado: string, editado: string) =>
      `${espacios} ${espacios === 1 ? 'espacio' : 'espacios'} · ${elevadores} ${elevadores === 1 ? 'elevador' : 'elevadores'} · creado ${creado} · editado ${editado}`,
    espacioDeTrabajo: 'Espacio de trabajo',
    carpeta: 'Carpeta',
    duplicar: 'Duplicar',
    duplicado: (n: string) => `Se duplicó como «${n}».`,
    copia: (n: string) => `${n} (copia)`,
    movido: (n: string, lugar: string) => `«${n}» está ahora en «${lugar}».`,
    guardado: 'Se guardaron los cambios del proyecto.',
    noSaleDeDatosReales:
      'Este proyecto está en un espacio de trabajo con datos reales: solo puede moverse a otro espacio de trabajo marcado «contiene datos reales».',
    importarDatosReales:
      'Este archivo está marcado «contiene datos reales»: impórtalo dentro de un espacio de trabajo con esa marca (créalo o elígelo a la izquierda).',
    // Exportación con datos reales
    exportarTitulo: '¿Exportar un proyecto con datos reales?',
    exportarTexto:
      'El archivo incluirá los datos reales del cliente y quedará marcado así. Guárdalo solo en lugares autorizados por el cliente y no lo compartas fuera de él.',
    exportarConfirmar: 'Exportar igual',
  },
  almacenamiento: {
    titulo: 'Almacenamiento en este navegador',
    uso: (usado: string, total: string) => `${usado} usados de ${total} disponibles`,
    desglose: (proyectos: string, versiones: string) =>
      `Proyectos: ${proyectos} · Historial de versiones: ${versiones}`,
    noDisponible: 'Este navegador no informa cuánto espacio usa.',
    protegido: 'Protegido: el navegador no lo borra para liberar espacio.',
    sinProteger:
      'Sin protección: si el equipo se queda sin espacio, el navegador podría borrar estos datos.',
    proteger: 'Proteger',
    protegerAyuda: 'Pide al navegador que no borre estos datos para liberar espacio',
    protegidoAhora: 'El navegador protegerá tus proyectos: no los borrará para liberar espacio.',
    rechazado:
      'El navegador no concedió la protección. Chrome la concede sola a los sitios que usas seguido o guardas en marcadores; mientras tanto, exporta respaldos.',
    limites: '¿Dónde viven tus datos?',
    limitesTitulo: 'Dónde viven tus datos',
    limitesLocal: 'Hoy (almacenamiento local, sin servidor ni cuenta)',
    limitesLocalLista: [
      'Los proyectos y su historial viven solo en este navegador de este equipo: otro navegador u otro equipo no los ve.',
      'Nadie más los ve ni se envían a internet; tampoco hay copia en la nube.',
      'Borrar los datos del sitio, una ventana de incógnito o reinstalar el navegador los elimina.',
      'El espacio lo decide el navegador (una parte del disco libre). El medidor muestra cuánto se usa.',
      'Respaldo: exporta cada proyecto (.gemba.json) cada semana y antes de cada presentación.',
    ],
    limitesRemoto: 'Más adelante (almacenamiento remoto, ola 3)',
    limitesRemotoLista: [
      'Proyectos en un servidor, con cuenta e inicio de sesión: se abren desde cualquier equipo y tienen copias de seguridad.',
      'Permite compartir con permisos por cliente, con seguridad del lado del servidor.',
      'No existe en esta versión: no lo presentes como un sistema multiusuario seguro.',
    ],
  },
} as const;
